import { useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import Cookies from 'js-cookie';
import { refreshToken } from '../../../../Redux/Auth/authActions';
import { EMPTY_PROJECT_MASTER_DATA } from '../Constants/projectMasterDataFields';
import { partnerPortalApi } from '../Services/partnerPortalApi';
import { extractProjectMasterDataFromResponse } from '../Utils/projectMasterDataUtils';
import { resolveAccessToken, usePartnerAccessToken } from './usePartnerAccessToken';

const isUnauthorizedError = (error) => {
  const message = (error?.message || '').toLowerCase();
  return message.includes('unauthorized') || message.includes('authentication');
};

export const useProjectMasterData = (enabled = true) => {
  const dispatch = useDispatch();
  const accessToken = usePartnerAccessToken();
  const [masterData, setMasterData] = useState(EMPTY_PROJECT_MASTER_DATA);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);

  const withAuthRetry = useCallback(async (apiCall) => {
    const token = resolveAccessToken(accessToken);
    if (!token) {
      throw new Error('Your session has expired. Please sign in again.');
    }

    try {
      return await apiCall(token);
    } catch (err) {
      if (!isUnauthorizedError(err) || !Cookies.get('refreshToken')) {
        throw err;
      }

      await dispatch(refreshToken());
      const refreshedToken = Cookies.get('accessToken');
      if (!refreshedToken) {
        throw new Error('Your session has expired. Please sign in again.');
      }

      return apiCall(refreshedToken);
    }
  }, [accessToken, dispatch]);

  const applyMasterDataResponse = useCallback((response) => {
    setMasterData(extractProjectMasterDataFromResponse(response));
  }, []);

  const refreshMasterData = useCallback(async () => {
    if (!enabled) {
      setMasterData(EMPTY_PROJECT_MASTER_DATA);
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await withAuthRetry((activeToken) => (
        partnerPortalApi.fetchProjectMasterData(activeToken)
      ));
      applyMasterDataResponse(response);
    } catch (err) {
      setError(err.message || 'Failed to load dropdown configuration.');
      setMasterData(EMPTY_PROJECT_MASTER_DATA);
    } finally {
      setLoading(false);
    }
  }, [applyMasterDataResponse, enabled, withAuthRetry]);

  useEffect(() => {
    refreshMasterData();
  }, [refreshMasterData]);

  const runMutation = useCallback(async (mutationCall) => {
    setSaving(true);
    setError(null);

    try {
      const response = await withAuthRetry(mutationCall);
      applyMasterDataResponse(response);
      return response;
    } finally {
      setSaving(false);
    }
  }, [applyMasterDataResponse, withAuthRetry]);

  const addItem = useCallback(async (field, value) => (
    runMutation((activeToken) => partnerPortalApi.addProjectMasterDataItem(activeToken, { field, value }))
  ), [runMutation]);

  const updateItem = useCallback(async (field, oldValue, newValue) => (
    runMutation((activeToken) => partnerPortalApi.patchProjectMasterDataItem(activeToken, {
      field,
      oldValue,
      newValue,
    }))
  ), [runMutation]);

  const deleteItem = useCallback(async (field, value) => (
    runMutation((activeToken) => partnerPortalApi.deleteProjectMasterDataItem(activeToken, { field, value }))
  ), [runMutation]);

  return {
    masterData,
    loading,
    error,
    saving,
    refreshMasterData,
    addItem,
    updateItem,
    deleteItem,
  };
};
